import { Form, Formik } from 'formik'
import numeral from 'numeral'
import { useState } from 'react'
import { Chart } from 'react-google-charts'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Card } from 'src/components/atoms/Card'
import { Image } from 'src/components/atoms/Image'
import { InternalLink } from 'src/components/atoms/Link'
import { Heading5 } from 'src/components/atoms/Typography'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { Tabs } from 'src/components/molecules/Tabs'

import { FormCard } from './FormCard'
import { IncludeOwnMeasurementsFormFields } from './IncludeOwnMeasurementsFormFields'
import { PlantingMixFormFields } from './PlantingMixFormFields'
import { StemsEmissionsFormFields } from './StemsEmissionsFormFields'
import { calculateCarbon, calculateGraphData, withDefaults } from './helper'
import { CalculatorTab, CarbonCalculatorValues } from './types'
import { validationSchema } from './validationSchema'

const CALCULATOR_TABS = [
  {
    label:
      'How many planted native trees would have the potential to balance my CO₂ emissions?',
    value: CalculatorTab.Carbon
  },
  {
    label:
      'How much CO₂ would my planted native trees remove after 50 years of growing?',
    value: CalculatorTab.Stems
  }
]

const TOOLTIP_CONTENT = {
  emissions: (
    <div>
      <Heading5>Carbon emissions</Heading5>
      <TypographyRebrand variant='paragraph'>
        Find out how much CO₂ you may be emitting using the{' '}
        <InternalLink
          color='lime'
          href='https://www.tanestrees.org.nz/resource-centre/carbon-calculator'
        >
          Tāne&apos;s Tree Trust calculator
        </InternalLink>
        . There are also many other carbon footprint calculators out there to
        use to get an estimate of your CO₂ footprint.
      </TypographyRebrand>
      <TypographyRebrand variant='paragraph'>
        <Heading5>Age of stand or timeframes</Heading5>
        Trees take some time from planting before they are large enough to make
        full use of sunlight, moisture and soil. The basis for all calculations
        are typically on a period of several decades. The standard timeframe is
        50 years to balance emissions for one year.
      </TypographyRebrand>
    </div>
  ),
  planting: (
    <div>
      <Heading5>Planting mix</Heading5>
      <TypographyRebrand variant='paragraph'>
        Trees are species which grow into tall trees such as totara, kauri, and
        rimu. Shrubs are smaller tree species that grow only 6-10m in height,
        such as mānuka. It is common practise when establishing native
        plantations to grow a mixture of trees and shrubs. The fast-growing
        shrubs provide shelter and protection for the tree species.
      </TypographyRebrand>
      <TypographyRebrand variant='paragraph'>
        We have added the default mix of 25% for trees and 75% for shrubs. You
        can change the mix if you have the information.
      </TypographyRebrand>
    </div>
  ),
  includeOwnMeasurements: (
    <div>
      <Heading5>Including your own measurements</Heading5>
      <TypographyRebrand variant='paragraph'>
        A calibration measurement involves collecting data from a specific stand
        to allow a more accurate prediction of carbon sequestered by that stand
        at the time of measurement survival = the percentage of planted trees of
        shrubs still living.
      </TypographyRebrand>
      <TypographyRebrand variant='paragraph'>
        <strong>Height</strong> = mean height of sterns
      </TypographyRebrand>
      <TypographyRebrand variant='paragraph'>
        <strong>DBH</strong> = Diameter at Breast Height (diameter taken at 1.4m
        above ground)
      </TypographyRebrand>
      <TypographyRebrand variant='paragraph'>
        <strong>RCD</strong> = Root Collar Diameter (diameter taken at 10 cm
        above ground)
      </TypographyRebrand>
    </div>
  )
}

export const CarbonCalculator = () => {
  const [co2Total, setCo2Total] = useState<number | undefined>()
  const [stems, setStems] = useState<number | undefined>()
  const [graphData, setGraphData] = useState<object | undefined>()

  const handleSubmit = (values: CarbonCalculatorValues) => {
    const { co2Total: calculatedCo2, stems: calculatedStems } = calculateCarbon(
      withDefaults(values)
    )

    setGraphData(calculateGraphData(values, calculatedStems))
    setCo2Total(calculatedCo2)
    if (calculatedStems) setStems(calculatedStems)
  }

  const formattedStems = stems && numeral(stems).format('0,0')
  const formattedCo2Total = co2Total && numeral(co2Total).format('0,0.00')

  const initialValues: CarbonCalculatorValues = withDefaults({
    formTab: CalculatorTab.Carbon,
    stems
  })

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ dirty, isValid, resetForm, setFieldValue, values }) => {
        const handleReset = () => {
          setCo2Total(undefined)
          setStems(undefined)
          setGraphData(undefined)
          resetForm()
        }

        const handleTabChange = (tab: string) => {
          handleReset()
          setFieldValue('formTab', tab)
        }

        return (
          <Form>
            <Card size='small' corner='square'>
              <Tabs
                tabs={CALCULATOR_TABS}
                onChange={handleTabChange}
                activeTab={values.formTab}
                ariaLabel='Carbon calculator navigation'
                className='[&_button]:normal-case! [&_button]:text-left! [&_button]:justify-start! lg:[&>div]:block! lg:[&>div]:w-full! lg:[&_.flex]:gap-4! lg:[&_button]:max-w-none! lg:[&_button]:grow-0! lg:[&_button]:shrink-0! lg:[&_button]:basis-[calc((100%_-_2rem)/3)]! [&_[aria-selected=true]]:text-forestDeep! [&_[aria-selected=true]]:border-b-forestDeep! [&_[aria-selected=true]]:bg-limeBright/20! [&_[aria-selected=false]]:text-forestDeep/70!'
              />
              <>
                <div className='lg:flex lg:space-x-4 space-y-4 lg:space-y-0 pt-4'>
                  <FormCard
                    heading={
                      values.formTab === CalculatorTab.Carbon
                        ? 'Carbon emissions and timeframe'
                        : 'Stems'
                    }
                    tooltipContent={TOOLTIP_CONTENT.emissions}
                  >
                    <StemsEmissionsFormFields />
                  </FormCard>
                  <FormCard
                    heading='Planting mix'
                    tooltipContent={TOOLTIP_CONTENT.planting}
                  >
                    <PlantingMixFormFields />
                  </FormCard>
                  <FormCard
                    heading='Do you have your own measurements?'
                    tooltipContent={TOOLTIP_CONTENT.includeOwnMeasurements}
                  >
                    <IncludeOwnMeasurementsFormFields />
                  </FormCard>
                </div>
                <div className='flex space-x-2 justify-center pt-4 pb-8'>
                  <ButtonRebrand
                    type='submit'
                    variant='lime'
                    disabled={!isValid || !dirty}
                  >
                    Calculate
                  </ButtonRebrand>
                  <ButtonRebrand variant='outline' onClick={handleReset}>
                    Reset
                  </ButtonRebrand>
                </div>
              </>

              {(formattedStems || formattedCo2Total) && (
                <Card
                  size='small'
                  corner='square'
                  className='bg-linen! mb-8 text-center'
                >
                  {values.formTab === CalculatorTab.Carbon && (
                    <>
                      <TypographyRebrand variant='h5' color='forestDeep'>
                        Native trees to plant or you could fund
                      </TypographyRebrand>
                      <div className='h-2 md:h-4' />
                      <TypographyRebrand variant='h3' color='forestDeep'>
                        <strong>{formattedStems}</strong> native trees
                      </TypographyRebrand>
                      <TypographyRebrand variant='paragraph' color='forestDeep'>
                        To remove {values.emissions} tonnes of CO₂ from the
                        atmosphere by age {values.age} years, you will need to
                        plant {formattedStems} native trees and shrubs.
                      </TypographyRebrand>
                    </>
                  )}
                  {values.formTab === CalculatorTab.Stems && (
                    <>
                      <TypographyRebrand
                        variant='introduction'
                        color='forestDeep'
                      >
                        CO₂ expected to be removed
                      </TypographyRebrand>
                      <div className='h-2 md:h-4' />
                      <TypographyRebrand variant='h3' color='forestDeep'>
                        <strong>{formattedCo2Total}</strong> tCO₂e
                      </TypographyRebrand>
                      <TypographyRebrand variant='paragraph' color='forestDeep'>
                        Your planting of {numeral(values.stems).format('0,0')}{' '}
                        native trees and shrubs is expected to remove{' '}
                        {formattedCo2Total} tonnes of CO₂ from the atmosphere
                        after {values.age} years.
                      </TypographyRebrand>
                    </>
                  )}
                  <Chart
                    chartType='AreaChart'
                    width='100%'
                    legendToggle
                    options={{
                      isStacked: 'absolute',
                      hAxis: {
                        title: 'Age (years)',
                        titleTextStyle: { italic: false },
                        gridlines: { count: 16 }
                      },
                      vAxis: {
                        title: 'Carbon (tCO\u2082)',
                        titleTextStyle: { italic: false },
                        minValue: 0
                      },
                      colors: ['#db7f23', '#5f9353'],
                      legend: {
                        position: 'top',
                        alignment: 'center'
                      }
                    }}
                    height='600px'
                    data={graphData}
                  />
                </Card>
              )}

              <div className='[&_.typography-rebrand-paragraph]:leading-snug'>
                <TypographyRebrand variant='paragraph' color='forestDeep'>
                  The carbon calculator we provide access to is owned, developed
                  and offered for use free-of-charge by not-for-profit charity,{' '}
                  <InternalLink
                    className='underline'
                    href='https://www.tanestrees.org.nz'
                  >
                    Tāne&apos;s Tree Trust.
                  </InternalLink>
                </TypographyRebrand>
                <TypographyRebrand variant='paragraph' color='forestDeep'>
                  This carbon calculator is not for use to determine an official
                  CO₂ offset, nor to determine benefits or liabilities under the
                  New Zealand Emissions Trading Scheme, or to make any claims of
                  carbon neutrality. The calculations only show the potential of
                  CO₂ absorption based on a default setting of trees growing
                  continuously for 50 years.
                </TypographyRebrand>
                <TypographyRebrand variant='paragraph' color='forestDeep'>
                  See more info about the CO₂ calculator, and the calculations
                  derived from it below.
                </TypographyRebrand>
              </div>
              <div className='flex flex-col items-center'>
                <TypographyRebrand variant='paragraph' color='forestDeep'>
                  Powered by
                </TypographyRebrand>
                <Image
                  src='/img/logos/ttt-logo.svg'
                  height={62}
                  width={300}
                  alt="Tāne's Tree Trust logo"
                />
              </div>
            </Card>
          </Form>
        )
      }}
    </Formik>
  )
}
